import { FormattedMessage } from 'react-intl';
import type { CSSProperties } from 'styled-components';
import styled, { keyframes } from 'styled-components';

import {
  ConversionCreditStatus,
  Currency,
  type SupportedCurrency,
} from '#src/__generated__/globalTypes.ts';
import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BodyM, BodyS } from '#src/atoms/typography/index.tsx';
import { Gauge } from '#src/atoms/ui/Gauge/index.tsx';
import { sportLogos } from '#src/components/SportLogo/index.tsx';
import { ConversionCreditPurchaseLink } from '#src/components/wallet/BankEthAccounting/ConversionCredits/ConversionCreditPurchaseLink/index.tsx';
import { useIntlContext } from '#src/contexts/intl/index.ts';
import { TimeLeft } from '#src/contexts/ticker/TimeLeft/index.tsx';
import { useAmountWithConversion } from '#src/hooks/useAmountWithConversion.ts';
import type { ConversionCreditWithAmounts } from '#src/hooks/useConversionCredits.tsx';
import { Sum } from '#src/hooks/useMonetaryAmount.ts';
import { isCryptocurrency } from '#src/lib/cryptocurrency.ts';
import { MonetaryAmount } from '#src/lib/monetaryAmount.ts';

const Root = styled(Vertical)`
  width: 100%;
  text-align: left;
  min-width: 180px;
`;

const Header = styled(Horizontal)`
  justify-content: space-between;
`;

const LogosContainer = styled(Horizontal)`
  :not(:first-child) {
    margin-left: calc(-1 * var(--unit));
  }
`;

const enterAnimation = keyframes`
  from {
    background-position: 0 0;
  }
`;

const Filled = styled.div`
  background: linear-gradient(
    to right,
    #ffa724 50%,
    #f2f3d0 50%,
    #f23b27 50%,
    #f23b27 100%
  );
  background-size: 200%;
  background-position: var(--discountPercentage) 0;
  transition: background-position 0.4s var(--c-cb-ease-out);
  height: 100%;
  border-radius: 3px;
  max-width: 100%;
  animation: ${enterAnimation} 0.8s normal 0.3s;
`;

const LogoWrapper = styled(Horizontal).attrs({ gap: 0, center: true })`
  width: var(--quadruple-unit);
  height: var(--quadruple-unit);
  border-radius: 50%;
  background-color: var(--c-white);
  border: 1px solid var(--c-nd-200);
  padding: 2px;

  svg {
    max-width: 100%;
    max-height: 100%;
  }
`;

export const OldConversionCredit = ({
  conversionCredit,
  referenceCurrency,
  spentConversionCreditMonetaryAmount,
  className,
}: {
  referenceCurrency: SupportedCurrency;
  conversionCredit: ConversionCreditWithAmounts;
  spentConversionCreditMonetaryAmount?: MonetaryAmount;
  className?: string;
}) => {
  const { formatNumber } = useIntlContext();

  const getProjectedConversionCreditAmountLeft = () => {
    if (!spentConversionCreditMonetaryAmount) {
      return conversionCredit.maxDiscount;
    }
    if (conversionCredit.singleUse) return MonetaryAmount.zero;

    return Sum([
      conversionCredit.maxDiscount,
      spentConversionCreditMonetaryAmount,
    ]);
  };

  const projectedConversionCreditAmountLeft =
    getProjectedConversionCreditAmountLeft();

  const primaryCurrency = isCryptocurrency(referenceCurrency)
    ? Currency.ETH
    : Currency.FIAT;

  const { main } = useAmountWithConversion({
    monetaryAmount: conversionCredit.maxDiscount,
    primaryCurrency,
  });

  const { main: totalMain } = useAmountWithConversion({
    monetaryAmount: conversionCredit.totalDiscount,
    primaryCurrency,
  });

  const { main: projectedMain } = useAmountWithConversion({
    monetaryAmount: projectedConversionCreditAmountLeft,
    primaryCurrency,
  });

  const percentage =
    (conversionCredit.maxDiscount.eurCents /
      conversionCredit.totalDiscount.eurCents) *
    100;

  const discountPercentage =
    (1 -
      projectedConversionCreditAmountLeft.eurCents /
        conversionCredit.maxDiscount.eurCents) *
    100;

  return (
    <Root
      className={className}
      style={
        { '--discountPercentage': `${discountPercentage}%` } as CSSProperties
      }
    >
      <Header>
        <BodyS color="var(--c-nd-600)" as="span">
          {conversionCredit.status === ConversionCreditStatus.USING ? (
            <BodyS color="var(--c-nd-600)">
              <ConversionCreditPurchaseLink
                purchase={conversionCredit.purchase}
              >
                <FormattedMessage
                  id="ConversionCredit.status.using"
                  defaultMessage="Currently used"
                />
              </ConversionCreditPurchaseLink>
            </BodyS>
          ) : (
            <>
              {!!conversionCredit.singleUse && (
                <FormattedMessage
                  id="conversionCredit.singleUse"
                  defaultMessage="Single use"
                />
              )}
            </>
          )}
        </BodyS>
        <BodyS color="var(--c-nd-600)" as="span">
          <TimeLeft time={conversionCredit.endDate} withExplicitTime />
        </BodyS>
      </Header>
      <Horizontal>
        {conversionCredit.sport ? (
          <LogoWrapper>{sportLogos[conversionCredit.sport]}</LogoWrapper>
        ) : (
          <LogosContainer gap={0}>
            {Object.values(sportLogos).map((logo, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <LogoWrapper key={i}>{logo}</LogoWrapper>
            ))}
          </LogosContainer>
        )}
        <BodyM color="var(--c-white)">
          {conversionCredit.percentageDiscount === 1 ? (
            <FormattedMessage
              id="conversionCredit.fullDiscount"
              defaultMessage="{maxDiscount} off"
              values={{
                maxDiscount: main,
              }}
            />
          ) : (
            <FormattedMessage
              id="conversionCredit.maxDiscount"
              defaultMessage="{percentageDiscount} off (up to {maxDiscount})"
              values={{
                percentageDiscount: formatNumber(
                  conversionCredit.percentageDiscount,
                  {
                    style: 'percent',
                    maximumFractionDigits: 0,
                  }
                ),
                maxDiscount: main,
              }}
            />
          )}
        </BodyM>
      </Horizontal>
      <Gauge>
        <Filled style={{ width: `${percentage}%` }} />
      </Gauge>
      <BodyS color="var(--c-nd-600)">
        <FormattedMessage
          id="conversionCredit.availableVsTotal"
          defaultMessage="{maxDiscount}/{totalDiscout} available"
          values={{
            maxDiscount: projectedMain,
            totalDiscout: totalMain,
          }}
        />
      </BodyS>
    </Root>
  );
};
